import React, { useEffect, useState } from "react";

/**
 * ServicesManager Page
 * - View services
 * - Edit service
 * - Update service
 * - Delete service
 * - Manage features list
 *
 * ⚠️ Update API endpoints if needed
 */

export default function ServicesManager() {
    const token = localStorage.getItem('token');
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState({
        title: "",
        icon: "",
        description: "",
        features: [],
    });

    // ---------- Fetch services ----------
    const fetchServices = async () => {
        try {
            setLoading(true);
            const res = await fetch(
                process.env.REACT_APP_API_URL + "/primedale_services", {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            }
            );
            const data = await res.json();

            const normalized = data.map((s) => ({
                ...s,
                features:
                    typeof s.features === "string"
                        ? JSON.parse(s.features || "[]")
                        : s.features || [],
            }));

            setServices(normalized);
        } catch (err) {
            console.error("Failed to fetch services", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchServices();
    }, []);

    // ---------- Start editing ----------
    const startEdit = (service) => {
        setEditingId(service.id);
        setForm({
            title: service.title || "",
            icon: service.icon || "",
            description: service.description || "",
            features: service.features || [],
        });
    };

    // ---------- Cancel editing ----------
    const cancelEdit = () => {
        setEditingId(null);
        setForm({ title: "", icon: "", description: "", features: [] });
    };

    // ---------- Handle form change ----------
    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    // ---------- Feature handlers ----------
    const addFeature = () => {
        setForm((prev) => ({ ...prev, features: [...prev.features, ""] }));
    };

    const updateFeature = (index, value) => {
        setForm((prev) => {
            const updated = [...prev.features];
            updated[index] = value;
            return { ...prev, features: updated };
        });
    };

    const removeFeature = (index) => {
        setForm((prev) => ({
            ...prev,
            features: prev.features.filter((_, i) => i !== index),
        }));
    };

    // ---------- Update service ----------
    const handleUpdate = async (id) => {
        try {
            await fetch(
                `${process.env.REACT_APP_API_URL}/primedale_services/${id}`,
                {
                    method: "PUT",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(form),
                }
            );

            cancelEdit();
            fetchServices();
        } catch (err) {
            console.error("Update failed", err);
        }
    };

    // ---------- Delete service ----------
    const handleDelete = async (id) => {
        if (!window.confirm("Delete this service?")) return;

        try {
            await fetch(
                `${process.env.REACT_APP_API_URL}/primedale_services/${id}`,
                { method: "DELETE" }
            );

            fetchServices();
        } catch (err) {
            console.error("Delete failed", err);
        }
    };

    // ---------- UI ----------
    return (
        <div className="container py-5">
            <h2 className="mb-4">Manage Services</h2>

            {loading && <p>Loading services...</p>}

            {!loading && (
                <div className="row g-4">
                    {services.map((service) => (
                        <div className="col-lg-6" key={service.id}>
                            <div className="card shadow-sm h-100">
                                <div className="card-body">
                                    {editingId === service.id ? (
                                        <>
                                            {/* -------- Edit Form -------- */}
                                            <input
                                                className="form-control mb-2"
                                                name="title"
                                                value={form.title}
                                                onChange={handleChange}
                                                placeholder="Title"
                                            />

                                            <input
                                                className="form-control mb-2"
                                                name="icon"
                                                value={form.icon}
                                                onChange={handleChange}
                                                placeholder="Icon class"
                                            />

                                            <textarea
                                                className="form-control mb-3"
                                                name="description"
                                                value={form.description}
                                                onChange={handleChange}
                                                placeholder="Description"
                                            />

                                            <h6>Features</h6>
                                            {form.features.map((f, i) => (
                                                <div key={i} className="d-flex gap-2 mb-2">
                                                    <input
                                                        className="form-control"
                                                        value={f}
                                                        onChange={(e) =>
                                                            updateFeature(i, e.target.value)
                                                        }
                                                    />
                                                    <button
                                                        className="btn btn-danger"
                                                        onClick={() => removeFeature(i)}
                                                    >
                                                        ✕
                                                    </button>
                                                </div>
                                            ))}

                                            <button
                                                className="btn btn-sm btn-secondary mb-3"
                                                onClick={addFeature}
                                            >
                                                + Add Feature
                                            </button>

                                            <div className="d-flex gap-2">
                                                <button
                                                    className="btn btn-success"
                                                    onClick={() => handleUpdate(service.id)}
                                                >
                                                    Save
                                                </button>
                                                <button
                                                    className="btn btn-outline-secondary"
                                                    onClick={cancelEdit}
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            {/* -------- View Mode -------- */}
                                            <i
                                                className={`fa fa-2x ${service.icon} mb-3 icon-color`}
                                            />
                                            <h5>{service.title}</h5>
                                            <p className="text-muted">{service.description}</p>

                                            <ul className="mb-3">
                                                {service.features?.map((f, i) => (
                                                    <li key={i}>{f}</li>
                                                ))}
                                            </ul>

                                            <div className="d-flex gap-2">
                                                <button
                                                    className="btn navyblue-bg btn-sm"
                                                    onClick={() => startEdit(service)}
                                                >
                                                    Edit
                                                </button>
                                                <button
                                                    className="btn btn-danger btn-sm"
                                                    onClick={() => handleDelete(service.id)}
                                                >
                                                    Delete
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
